import { Fragment } from "react";
import type { GpuSeries } from "../../types";

interface Props {
  gpus: GpuSeries[];
  nodeOrder: string[];
  selected?: string;
  onSelect?: (g: GpuSeries) => void;
}

const last = (arr: number[]): number => (arr.length ? arr[arr.length - 1] : 0);

export function GPUHeatmap({ gpus, nodeOrder, selected, onSelect }: Props) {
  const columns = Math.max(2, Math.max(...nodeOrder.map((n) => gpus.filter((g) => g.node === n).length)));
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `72px repeat(${columns}, 1fr)`,
        gap: 4,
      }}
    >
      <div />
      {Array.from({ length: columns }).map((_, i) => (
        <div
          key={i}
          style={{ fontSize: 9, letterSpacing: 1, color: "var(--fg-3)", textAlign: "center" }}
        >
          GPU {i}
        </div>
      ))}

      {nodeOrder.map((nn) => {
        const row = gpus
          .filter((g) => g.node === nn)
          .sort((a, b) => a.gpu_id - b.gpu_id);
        return (
          <Fragment key={nn}>
            <div
              style={{
                fontSize: 10,
                fontFamily: "var(--mono)",
                color: "var(--fg-2)",
                display: "flex",
                alignItems: "center",
              }}
            >
              {nn.replace("hpc-", "")}
            </div>
            {row.map((g) => {
              const u = last(g.util);
              const t = last(g.temp_c);
              const p = last(g.power_w);
              const m = last(g.mem_mb);
              const isXid = g.status === "xid-faulted";
              const bg = isXid
                ? "rgba(255,95,122,0.28)"
                : `rgba(78,225,240,${0.1 + (u / 100) * 0.5})`;
              const border = isXid
                ? "var(--rose)"
                : t > 82
                ? "var(--amber)"
                : u > 60
                ? "rgba(78,225,240,0.5)"
                : "var(--line-2)";
              const sel = selected === g.id;
              return (
                <button
                  key={g.id}
                  onClick={() => onSelect?.(g)}
                  style={{
                    background: bg,
                    border: `1px solid ${border}`,
                    borderRadius: 4,
                    padding: "8px 10px",
                    textAlign: "left",
                    height: 58,
                    outline: sel ? "1.5px solid var(--cyan)" : "none",
                    outlineOffset: -1,
                    cursor: "pointer",
                    position: "relative",
                  }}
                >
                  <div
                    style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}
                  >
                    <span
                      className="mono"
                      style={{ fontSize: 14, color: "var(--fg-0)", fontWeight: 500 }}
                    >
                      {Math.round(u)}%
                    </span>
                    <span
                      className="mono"
                      style={{
                        fontSize: 9,
                        color: t > 82 ? "var(--amber)" : "var(--fg-2)",
                      }}
                    >
                      {t.toFixed(0)}°C
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: 9,
                      color: "var(--fg-3)",
                      fontFamily: "var(--mono)",
                      marginTop: 2,
                    }}
                  >
                    {(p / 1000).toFixed(2)}kW · {Math.round(m / 1024)}/{Math.round(g.mem_total_mb / 1024)}G
                  </div>
                  {isXid && (
                    <div
                      style={{
                        position: "absolute",
                        top: 3,
                        right: 3,
                        fontSize: 8,
                        color: "var(--rose)",
                        fontWeight: 700,
                        letterSpacing: 1,
                      }}
                      className="pulse"
                    >
                      XID
                    </div>
                  )}
                </button>
              );
            })}
            {Array.from({ length: Math.max(0, columns - row.length) }).map((_, i) => (
              <div key={`pad-${nn}-${i}`} />
            ))}
          </Fragment>
        );
      })}
    </div>
  );
}
